import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import { DescriptionCol, DescriptionRow } from '@/components/Description';
import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { useUpdateBasicTransactionItem } from '@/context/services/transaction_items/List/BasicTransactionItem.service';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { RequiredSettingCheckbox } from '@/features/transaction_items/components/Form/GeneralForm/RequiredSettingCheckbox';
import { useTranslation } from '@/i18n';
import {
  Form,
  Label,
  Loading,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ComponentPreview } from './ComponentPreview';
import styles from './TransactionItemGeneralForm.module.scss';

const cx = classNames.bind(styles);

export type TransactionItemBasicFormType = {
  is_required: boolean;
};

export type Props = {
  mode: TransactionItemDrawerMode;
  isLoading: boolean;
  showLoading: (loading: boolean) => void;
  onShowUpdateConfirm: (show: boolean) => void;
  onSetMode: (mode: TransactionItemDrawerMode) => void;
  handleSuccess: () => Promise<void>;
  itemId?: string;
  itemLabel?: string;
  itemPlaceholder?: string;
  itemTooltip?: string;
  itemIsRequired?: boolean;
};

export const TransactionItemBasicForm: FC<Props> = memo(
  ({
    mode,
    isLoading,
    showLoading,
    onShowUpdateConfirm,
    onSetMode,
    itemId,
    itemLabel,
    itemPlaceholder,
    itemTooltip,
    itemIsRequired,
  }) => {
    const defaultValues = {
      is_required: itemIsRequired,
    };

    const formProps = useForm<TransactionItemFormType>({
      mode: 'onChange',
      defaultValues,
    });

    const notify = useToastAPI();
    const { t } = useTranslation();
    const { report_type_id: reportTypeId } = useParams();

    const [{ setError }, InlineError, InlineErrorProvider] =
      useErrorInlineNotification();

    const { mutateAsync: mutateTransactionItem } =
      useUpdateBasicTransactionItem(reportTypeId);

    const isDirty = formProps.formState.isDirty;

    const showSuccessToast = useCallback(
      async (message: string) => {
        onSetMode(TransactionItemDrawerModeEnum.DETAIL);
        notify.success({
          title: message,
          duration: 5,
        });
      },
      [notify, onSetMode]
    );

    const showErrorToast = useCallback(
      (title: string) => {
        notify.error({
          title: title,
          duration: 5,
        });
      },
      [notify]
    );

    const onSubmit = async () => {
      showLoading(true);

      try {
        const result = await mutateTransactionItem(itemId, {
          is_required: formProps.getValues('is_required'),
        });

        if (result.data) {
          showSuccessToast(t('transaction_items.update.success'));
        } else if (result.isForbiddenMimicError) {
          setError(new Error(result.messages?.join('\n')));
        } else {
          showErrorToast(result.messages.join('\n'));
        }
      } catch {
        showErrorToast(t('transaction_items.update.error'));
      } finally {
        showLoading(false);
      }
    };

    useEffect(() => {
      if (mode === TransactionItemDrawerModeEnum.EDIT) {
        onShowUpdateConfirm(isDirty);
      }
    }, [isDirty, mode, onShowUpdateConfirm]);

    return (
      <>
        {isLoading && <Loading size='xlarge' isOverlay color='white' />}

        <Form<TransactionItemFormType>
          id='transaction_item_form'
          formMethod={formProps}
          onSubmit={onSubmit}
        >
          <div className={cx(styles['transaction-item-form'])}>
            <InlineErrorProvider value={setError}>
              {InlineError.props.visible && (
                <div className={cx(styles['inline-error'])}>{InlineError}</div>
              )}
            </InlineErrorProvider>

            {/* Label */}
            {/*********************************************** */}
            <DescriptionRow>
              <DescriptionCol
                label={
                  <Label testId='label'>
                    <BoldLabelText>
                      {t('transaction_items.table.label')}
                    </BoldLabelText>
                  </Label>
                }
                labelWidth='176px'
              />
              <DescriptionCol
                value={
                  <div
                    data-testid='label-detail'
                    className={cx(styles['label-detail'])}
                  >
                    {itemLabel}
                  </div>
                }
              />
            </DescriptionRow>

            {/* Custom Or Basic */}
            {/*********************************************** */}
            <DescriptionRow>
              <DescriptionCol
                label={
                  <Label testId='customizable'>
                    <BoldLabelText>
                      {t('transaction_items.table.customizable')}
                    </BoldLabelText>
                  </Label>
                }
                labelWidth='176px'
              />
              <DescriptionCol
                value={<div>{t('transaction_items.table.master')}</div>}
              />
            </DescriptionRow>

            {/* Input Type */}
            {/*********************************************** */}
            <DescriptionRow>
              <DescriptionCol
                label={
                  <Label testId='component_type-value'>
                    <BoldLabelText>
                      {t('transaction_items.table.component_type')}
                    </BoldLabelText>
                  </Label>
                }
                labelWidth='176px'
              />
              <DescriptionCol
                value={
                  <div>
                    <div>
                      {t('transaction_items.drawer.component_type.text')}
                    </div>
                  </div>
                }
              />
            </DescriptionRow>

            {/* Required or Optional */}
            {/*********************************************** */}
            <RequiredSettingCheckbox mode={mode} formProps={formProps} />

            {/* Preview */}
            {/*********************************************** */}
            <ComponentPreview
              componentType='text'
              label={itemLabel}
              placeholder={itemPlaceholder}
              tooltip={itemTooltip}
              isRequired={formProps.watch('is_required')}
            />
          </div>
        </Form>
      </>
    );
  }
);
TransactionItemBasicForm.displayName = 'TransactionItemBasicForm';
